import { z } from 'zod';
import { pixelRegionSchema } from '../macroCalibration';
import { MACRO_COLOR_POLICY_V1 } from './colorPolicy';

const digest = z.string().regex(/^[0-9a-f]{64}$/);
const boundedId = z.string().min(1).max(128);
const safeNonnegative = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const origin = z.enum(['stream-tag', 'capture-api', 'legacy-recorder-rule', 'reference-rendering-policy']);
export const colorFailureCodeSchema = z.enum([
  'color_metadata_missing', 'color_metadata_conflict', 'color_interpretation_unsupported', 'color_pixels_invalid',
  'color_precision_unavailable', 'color_generation_stale', 'color_provenance_mismatch',
  'color_profile_recompile_required', 'required_positive_miss', 'color_evidence_inseparable', 'color_validation_incomplete',
]);
export const colorInterpretationSchema = z.object({
  matrix: z.enum(['rgb', 'bt601', 'bt709', 'bt2020-ncl']),
  primaries: z.enum(['bt601-525', 'bt601-625', 'bt709', 'display-p3', 'bt2020']),
  transfer: z.enum(['srgb', 'bt1886', 'bt709-oetf', 'pq', 'hlg', 'linear']),
  range: z.enum(['full', 'limited']), bitDepth: z.union([z.literal(8), z.literal(10), z.literal(12), z.literal(16), z.literal(32)]),
  chromaLocation: z.enum(['none', 'left', 'center', 'top-left']),
  referenceWhiteNits: z.number().finite().positive().max(10_000), peakNits: z.number().finite().positive().max(10_000),
  hlgSystemGamma: z.number().finite().min(1).max(1.5).optional(),
}).strict().superRefine((value, context) => {
  if ((value.transfer === 'hlg') !== (value.hlgSystemGamma !== undefined)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['hlgSystemGamma'], message: 'HLG gamma must be present only for HLG' });
  }
  if (value.peakNits < value.referenceWhiteNits) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['peakNits'], message: 'Peak is below reference white' });
  }
});
const originsBase = {
  matrix: origin, primaries: origin, transfer: origin, range: origin, bitDepth: origin,
  chromaLocation: origin, referenceWhiteNits: origin, peakNits: origin,
};
const identityBase = {
  version: z.literal(1), interpretation: colorInterpretationSchema, colorGeneration: safeNonnegative,
  recipe: z.literal(MACRO_COLOR_POLICY_V1.recipe), recipeDigest: digest,
  comparisonWhiteNits: z.literal(MACRO_COLOR_POLICY_V1.comparisonWhiteNits),
};
const recordedColorIdentity = z.object({ ...identityBase, kind: z.literal('recorded'), mediaSha256: digest,
  metadataDigest: digest, origins: z.object(originsBase).strict() }).strict();
const liveColorIdentity = z.object({ ...identityBase, kind: z.literal('live'), captureSessionId: boundedId,
  captureIdentityDigest: digest, origins: z.object(originsBase).strict() }).strict();
const withHlgOrigins = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict().superRefine((value, context) => {
  const hlg = (value as { interpretation: { transfer: string }; origins: { hlgSystemGamma?: string } }).interpretation.transfer === 'hlg';
  const hasOrigin = (value as { origins: { hlgSystemGamma?: string } }).origins.hlgSystemGamma !== undefined;
  if (hlg !== hasOrigin) context.addIssue({ code: z.ZodIssueCode.custom, path: ['origins', 'hlgSystemGamma'], message: 'HLG origin mismatch' });
});
export const colorIdentitySchema = z.union([
  withHlgOrigins({ ...recordedColorIdentity.shape, origins: z.object({ ...originsBase, hlgSystemGamma: origin.optional() }).strict() }),
  withHlgOrigins({ ...liveColorIdentity.shape, origins: z.object({ ...originsBase, hlgSystemGamma: origin.optional() }).strict() }),
]);
export const colorFrameIdentitySchema = z.object({ source: colorIdentitySchema, frameSeq: safeNonnegative, ptsUs: safeNonnegative }).strict();
export const canonicalFrameRequestSchema = z.object({
  packageId: boundedId, manifestSha256: digest, mediaSha256: digest, frameSeq: safeNonnegative, ptsUs: safeNonnegative,
  colorIdentityDigest: digest, region: pixelRegionSchema.optional(),
}).strict();
export const itpChannelLimitSchema = z.object({ numerator: safeNonnegative, denominator: z.number().int().positive().safe() }).strict()
  .refine(value => value.numerator <= value.denominator, { message: 'Channel limit exceeds one', path: ['numerator'] });
export const readyItpNormalizationSchema = z.object({
  profile: z.literal(MACRO_COLOR_POLICY_V1.profile), readiness: z.literal('ready'), scoringRule: z.literal(MACRO_COLOR_POLICY_V1.scoringRule),
  recommendedThresholdPercent: z.number().int().min(5).max(99),
  enabledChannels: z.object({ color: z.literal(true), structure: z.boolean(), detail: z.boolean(), frequency: z.boolean() }).strict(),
  limits: z.object({ color: itpChannelLimitSchema, structure: itpChannelLimitSchema,
    detail: itpChannelLimitSchema, frequency: itpChannelLimitSchema }).strict(), trainingDigest: digest,
}).strict().refine(value => value.enabledChannels.structure || value.enabledChannels.detail || value.enabledChannels.frequency,
  { message: 'A structural channel must be enabled', path: ['enabledChannels'] });

export { digest as macroColorDigestSchema };
